import CloseIcon from '@mui/icons-material/Close'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import { FC, ReactNode, useEffect, useState } from 'react'
import { getPxAsRem } from 'shared/utils'
import { StyledHeader } from './styled'

interface DrawerComponentProps {
    children: ReactNode
    open?: boolean
    onClose?: () => void
    anchor?: 'left' | 'right' | 'top' | 'bottom'
    primaryActionText?: string
    onPrimaryAction?: () => void
    hasCancelButton?: boolean
    onCancel?: () => void
    headerTitle?: string
    saveButtonDisabled?: boolean
    variant?: 'persistent' | 'permanent' | 'temporary'
    hasSaveButton?: boolean
    width?: string | number
}

const DrawerComponent: FC<DrawerComponentProps> = ({
    children,
    open,
    onClose,
    headerTitle,
    primaryActionText,
    onPrimaryAction,
    hasCancelButton,
    onCancel,
    saveButtonDisabled,
    hasSaveButton = true,
    width = getPxAsRem(515),
}) => {
    const [minimized, setMinimized] = useState(false)

    useEffect(() => {
        if (!open) setMinimized(false)
    }, [open])

    useEffect(() => {
        if (minimized) setMinimized(false)
    }, [children])

    const hidden = !open || minimized

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1200,
                pointerEvents: hidden ? 'none' : 'auto',
            }}
        >
            {/* backdrop + drawer panel */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: hidden ? 'none' : 'auto',
                }}
            >
                {/* backdrop */}
                <Box
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        opacity: hidden ? 0 : 1,
                        transition: 'opacity 200ms ease-in-out',
                    }}
                />
                {/* drawer panel */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: getPxAsRem(24),
                        overflow: 'hidden',
                        opacity: hidden ? 0 : 1,
                        transform: hidden
                            ? 'translateX(20px)'
                            : 'translateX(0)',
                        transition:
                            'opacity 200ms ease-in-out, transform 200ms ease-in-out',
                    }}
                >
                    <StyledHeader>
                        <Typography variant="h4">{headerTitle}</Typography>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <IconButton
                                size="small"
                                onClick={() => setMinimized(true)}
                            >
                                <ExpandMoreIcon />
                            </IconButton>
                            <IconButton size="small" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </StyledHeader>
                    <Stack sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                        {children}
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={2}
                        marginTop={'auto'}
                        pt={5}
                    >
                        {hasCancelButton && (
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                        )}
                        {hasSaveButton && (
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={onPrimaryAction}
                                disabled={saveButtonDisabled}
                            >
                                {primaryActionText}
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Box>

            {/* minimized bar */}
            <Box
                onClick={() => setMinimized(false)}
                sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    minWidth: '200px',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    boxShadow: 6,
                    px: 2,
                    py: 1,
                    display: minimized && open ? 'flex' : 'none',
                    pointerEvents: 'auto',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                }}
            >
                <ExpandLessIcon
                    sx={{ fontSize: 16, color: 'text.secondary' }}
                />
                <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    sx={{ maxWidth: 200 }}
                >
                    {headerTitle}
                </Typography>
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation()
                        onClose?.()
                    }}
                    sx={{ ml: 'auto' }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        </Box>
    )
}

export default DrawerComponent
